  import axios from "axios";
  import { BaseURL } from "./baseURL";
  import { refreshTokenApi } from "../apis/authApi";
  import { toast } from "react-toastify";

  // ────────────────────────────────────────────────
  // Axios Instance
  // ────────────────────────────────────────────────
  const apiClient = axios.create({
    baseURL: BaseURL,
    withCredentials: true,
    timeout: 15000,
  });

  // ────────────────────────────────────────────────
  // Global refresh coordination
  // ────────────────────────────────────────────────
  let isRefreshing = false;
  let failedQueue = [];

  // Resolve/reject queued requests after refresh
  const processQueue = (error = null, token = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    failedQueue = [];
  };

  // ────────────────────────────────────────────────
  // Request Interceptor
  // Attach Authorization header except auth endpoints
  // ────────────────────────────────────────────────
  apiClient.interceptors.request.use(
    (config) => {
      const skipAuthHeader =
        config.url?.includes("/auth/token/refresh/") ||
        config.url?.includes("/auth/login/");

      if (!skipAuthHeader) {
        const token = sessionStorage.getItem("accessToken");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // ────────────────────────────────────────────────
  // Response Interceptor – Auto Refresh on 401
  // ────────────────────────────────────────────────
  apiClient.interceptors.response.use(
    (response) => response,

    async (error) => {
      const originalRequest = error.config;

      // If not 401 → let useAxios handle formatting
      if (error.response?.status !== 401) {
        return Promise.reject(error);
      }

      // Already retried once → logout user
      if (originalRequest._retry) {
        sessionStorage.clear();

        toast.error(
          <div>
            <strong>Session Expired</strong>
            <div>Please log in again.</div>
          </div>
        );

        window.location.replace("/login");
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      // If refresh already in progress → queue request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        const refreshToken = sessionStorage.getItem("refreshToken");
        if (!refreshToken) {
          throw new Error("No refresh token available");
        }

        // Call refresh endpoint (no auth header attached)
        const refreshData = await refreshTokenApi(refreshToken);

        const newAccess =
          refreshData.access ||
          refreshData.access_token ||
          refreshData.data?.access ||
          refreshData.tokens?.access;

        const newRefresh =
          refreshData.refresh ||
          refreshData.refresh_token ||
          refreshData.data?.refresh ||
          refreshData.tokens?.refresh;

        if (!newAccess) {
          throw new Error("Refresh response missing access token");
        }

        // Update storage
        sessionStorage.setItem("accessToken", newAccess);
        if (newRefresh) {
          sessionStorage.setItem("refreshToken", newRefresh);
        }

        // Update default header
        apiClient.defaults.headers.common.Authorization = `Bearer ${newAccess}`;

        // Retry original request
        originalRequest.headers.Authorization = `Bearer ${newAccess}`;

        // Resolve queued requests
        processQueue(null, newAccess);

        return apiClient(originalRequest);
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);

        processQueue(refreshError);

        sessionStorage.clear();

        toast.error(
          <div>
            <strong>Session Expired</strong>
            <div>Please log in again.</div>
          </div>
        );

        window.location.replace("/login");

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );

  export default apiClient;